import { Component, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UserAccountService } from '../../../common/service/user-account.service';
import { PagedObjectManagerComponent } from '../../../core/component/paged-object-manager/paged-object-manager';
import { FilterDefinitionItem } from '../../../core/model/filter-definition-item';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { CommandFactoryService } from '../../../core/service/factory/command-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { mailCommand } from '../../../message/model/const/command';
import { leaseAgreementCommand } from '../../command/lease-agreement.command';
import { CustomPropertyVisibilityType, LeaseAgreementStatus, SubleaseStatus, SubleaseStatusToString } from '../../model/const/enums';
import { CooperativeHousingContract } from '../../model/cooperative-housing-contract';
import { ContractService } from '../../service/base/contract';
import { ApproveContractCommandHandler } from '../../service/command/approve-contact-command-handler.service';
import { ContractCommandHandlerResolver } from '../../service/command/contract-command-handler-resolver.service';
import { CooperativeHousingContractCommandHandlerResolver } from '../../service/command/cooperative-housing-contract-command-handler-resolver.service';
import { ESignKeyRecieptCommandHandler } from '../../service/command/esign-key-reciept-document-command-handler.service';
import { ESignLeaseAgreementContractCommandHandler } from '../../service/command/esign-lease-agreement-document-command-handler.service';
import { CooperativeHousingContractCommandFactory } from '../../service/command/factory/cooperative-housing-contract-command-factory.service';
import { PrintContractCommandHandler } from '../../service/command/print-contract-command-handler.service';
import { ShowLeaseAgreementInvoicesCommandHandler } from '../../service/command/show-lease-agreement-invoices-command-handler';
import { TerminateContractCommandHandler } from '../../service/command/terminate-contract-command-handler.service';
import { TransferCooperativeHousingCommandHandler } from '../../service/command/transfer-cooperative-housing-command-handler.service';
import { VoidContractCommandHandler } from '../../service/command/void-contract-command-handler.service';
import { WithdrawApprovalCommandHandler } from '../../service/command/withdraw-approval-command-handler.service';
import { WithdrawTerminationCommandHandler } from '../../service/command/withdraw-termination-command-handler.service';
import { ColumnDefinitionService } from '../../service/data/column-definition.service';
import { CooperativeHousingContractService } from '../../service/data/cooperative-housing-contract.service';
import { GetInvoiceDeliveryMethodIcon, InvoiceDeliveryMethod, InvoiceDeliveryMethodToString } from '../../../invoice/model/const/enum';
import { CustomPropertyService } from '../../service/data/custom-property.service';
import { CustomPropertyHelper } from '../../helper/customproperties-helper';
import { PropertyService } from '../../service/data/property.service';
import { CompanyService } from '../../service/data/company.service';
import { LeaseAgreement } from '../../model/lease-agreement';
import { Features, ModuleNames } from '../../../common/model/account-definition';
import { standardCommand } from '../../../core/model/command';
import { CooperativeHousingLeaseAgreementListService } from '../../service/data/lease-agreement-list.service';

@Component({
    selector: 'app-cooperative-housing-contract-manager',
    templateUrl: './../../../core/component/paged-object-manager/paged-object-manager.html',
    styleUrls: ['./../../../core/component/paged-object-manager/paged-object-manager.scss'],
    providers: [
        ApproveContractCommandHandler,
        CooperativeHousingContractCommandHandlerResolver,
        ShowLeaseAgreementInvoicesCommandHandler,
        TerminateContractCommandHandler,
        VoidContractCommandHandler,
        ESignLeaseAgreementContractCommandHandler,
        WithdrawApprovalCommandHandler,
        WithdrawTerminationCommandHandler,
        PrintContractCommandHandler,
        ESignKeyRecieptCommandHandler,
        TransferCooperativeHousingCommandHandler,
        CooperativeHousingLeaseAgreementListService,
        { provide: ContractService, useExisting: CooperativeHousingContractService },
        { provide: CommandFactoryService, useClass: CooperativeHousingContractCommandFactory },
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CooperativeHousingContractManagerComponent extends PagedObjectManagerComponent<CooperativeHousingContract> {
  constructor(
    activatedRoute: ActivatedRoute,
    columnDefinitionService: ColumnDefinitionService,
    dispatcher: CommandDispatcher,
    handlerResolver: CooperativeHousingContractCommandHandlerResolver,
    service: CooperativeHousingContractService,
    messageService: MessageService,
    navigationService: NavigationService,
    private _userAccount: UserAccountService,
    private readonly _customPropertyService: CustomPropertyService,
    private readonly _ch: CustomPropertyHelper,
    private readonly _companyService: CompanyService,
    private readonly _propertyService: PropertyService,
    private readonly _leaseAgreementListService: CooperativeHousingLeaseAgreementListService
  ) {
    super(service, messageService, navigationService, dispatcher, handlerResolver, _leaseAgreementListService);
    this.columnDefinition = columnDefinitionService.create('CooperativeHousingContract');
    this.title = 'LEASE_AGREEMENT.COOPERATIVE_HOUSING_LIST_HEADER';
    this.preferencesKey = 'rent.cooperative-housing-contract.list';

    this.AddSubscription(
      _userAccount.current.subscribe({
        next: account => {
          if (account.hasCooperativeAgreementWriteAccess()) {
            this.addContextMenuItemDefinition('LEASE_AGREEMENT.COMMAND.TRANSFER', leaseAgreementCommand.transferCooperativeHousing);
          } else {
            this.allowNew = false;
          }
          if (account.hasCooperativeAgreementWriteAccess()) {
            this.addContextMenuItemDefinition('LEASE_AGREEMENT.COMMAND.APPROVE_TRANSFER', leaseAgreementCommand.approve);
          }
          if (account.hasCooperativeAgreementWriteAccess()) {
            this.addContextMenuItemDefinition('LEASE_AGREEMENT.COMMAND.WITHDRAW_APPROVAL', leaseAgreementCommand.withdrawApproval);
          }
          if (account.hasCooperativeAgreementWriteAccess()) {
            this.addContextMenuItemDefinition('LEASE_AGREEMENT.COMMAND.CREATE_INVOICE', leaseAgreementCommand.createInvoice);
          }
          this.addContextMenuItemDefinition('LEASE_AGREEMENT.COMMAND.SHOW_INVOICES', leaseAgreementCommand.showInvoices);
          if (account.hasCooperativeAgreementWriteAccess()) {
            this.addContextMenuItemDefinition('LEASE_AGREEMENT.COMMAND.LINK_DOCUMENT', leaseAgreementCommand.linkDocument);
          }
          this.addContextMenuItemDefinition('LEASE_AGREEMENT.COMMAND.SHOW_DOCUMENTS', leaseAgreementCommand.showDocuments);
          if (account.hasCooperativeAgreementWriteAccess()) {
            this.addContextMenuItemDefinition('LEASE_AGREEMENT.COMMAND.VOID', leaseAgreementCommand.void);
          }

          if (account.hasAccess(ModuleNames.Messages, Features.ManageEmail)) {
            this.addListMenuItem('LEASE_AGREEMENT.COMMAND.SEND_EMAIL', mailCommand.sendTenantEmail);
          }
          if (account.hasAccess(ModuleNames.Messages, Features.ManageMail)) {
            this.addListMenuItem('LEASE_AGREEMENT.COMMAND.SEND_LETTER', mailCommand.sendTenantMail);
          }
          if (account.hasAccess(ModuleNames.Messages, Features.ManageSms)) {
            this.addListMenuItem('LEASE_AGREEMENT.COMMAND.SEND_SMS', mailCommand.sendTenantSMS);
          }
          this.addListMenuItem('COMMON.EXCEL_EXPORT', leaseAgreementCommand.exportNoPersonalData);
          this.addListMenuItem('COMMON.COMMAND.EXPORT_EXCEL_WITH_PERSONAL_DATA', leaseAgreementCommand.export);
        },
      })
    );
  }

  ngOnInit() {
    this.AddSubscription(
      this._leaseAgreementListService.getFilterDefinition().subscribe(definition => {
        this._customPropertyService.getList().subscribe(customProperties => {
          this.addFilterDefinitions(definition);

          const customPropertiesFilters = this._ch.generateCustomPropertyFilters(
            customProperties,
            definition,
            CustomPropertyVisibilityType.Company | CustomPropertyVisibilityType.Property | CustomPropertyVisibilityType.Building
          );
          this._ch.createCustomPropertyColumnDefinition(
            customProperties,
            this.columnDefinition,
            CustomPropertyVisibilityType.Company | CustomPropertyVisibilityType.Property | CustomPropertyVisibilityType.Building
          );
          this.filterDefinition = [...this.filterDefinition, ...customPropertiesFilters];
        });
      })
    );

    this.AddSubscription(
      this._userAccount.current.subscribe({
        next: account => {
          if (account.hasCooperativeAgreementWriteAccess()) {
            this.addContextMenuItemDefinition('EDIT_BTN', standardCommand.edit);
            this.addContextMenuItemDefinition('DELETE_BTN', standardCommand.delete);
          } else {
            this.addContextMenuItemDefinition('EDIT_BTN', standardCommand.edit);
          }
        },
      })
    );
  }

  override filterInternal(filterState: any): void {
    this._ch.applyCustomPropertyFilters(filterState);
  }

  protected addFilterDefinitions(definition: any) {
    this.defaultFilters = definition.defaultFields;
    const articles = definition.articles.map(article => {
      return { caption: article.toString(), value: article.id };
    });
    articles.splice(0, 0, { caption: 'ALL_TEXT', value: '' });

    this.filterDefinition = [
      new FilterDefinitionItem(
        'LEASE_AGREEMENT.STATUS',
        'status',
        definition.status,
        [
          { caption: 'ALL_TEXT', value: 0 },
          { caption: 'CONST.LEASE_AGREEMENT_STATUS_ACTIVE', value: 9 },
          { caption: 'CONST.LEASE_AGREEMENT_STATUS_DRAFT', value: LeaseAgreementStatus.Draft },
          { caption: 'CONST.LEASE_AGREEMENT_STATUS_COMING', value: LeaseAgreementStatus.Coming },
          { caption: 'CONST.LEASE_AGREEMENT_STATUS_VALID', value: LeaseAgreementStatus.Valid },
          { caption: 'CONST.LEASE_AGREEMENT_STATUS_TERMINATED', value: LeaseAgreementStatus.Terminated },
          { caption: 'CONST.LEASE_AGREEMENT_STATUS_EXPIRED', value: LeaseAgreementStatus.Expired },
          { caption: 'CONST.LEASE_AGREEMENT_STATUS_VOIDED', value: LeaseAgreementStatus.Voided },
        ],
        definition.selectedFields.includes('status')
      ),
      new FilterDefinitionItem(
        'COMPANY.LIST_HEADER',
        'company',
        definition.company,
        this._companyService,
        definition.selectedFields.includes('company')
      ),
      new FilterDefinitionItem(
        'RENTAL_OBJECT_FORM.PROPERTY_LABEL',
        'property',
        definition.property,
        this._propertyService,
        definition.selectedFields.includes('property')
      ),
      new FilterDefinitionItem(
        'COOPERATIVE_HOUSING.OWNER',
        'tenantContactId',
        definition.tenantContactId,
        null,
        definition.selectedFields.includes('tenantContactId')
      ),
      new FilterDefinitionItem(
        'TENANT.CIVIC_ORG_NUMBER',
        'tenantContactIdentification',
        definition.tenantContactIdentification,
        null,
        definition.selectedFields.includes('tenantContactIdentification')
      ),
      new FilterDefinitionItem(
        'RENTAL_OBJECT_LIST.STREET_COLUMN_BRF',
        'street',
        definition.street,
        null,
        definition.selectedFields.includes('street')
      ),
      new FilterDefinitionItem(
        'LEASE_AGREEMENT.LEASE_AGREEMENT_NUMBER',
        'leaseAgreementNumber',
        definition.leaseAgreementNumber,
        null,
        definition.selectedFields.includes('leaseAgreementNumber')
      ),
      new FilterDefinitionItem(
        'RENTAL_OBJECT_FORM.RENTAL_OBJECT_NUMBER_LABEL',
        'rentalId',
        definition.rentalId,
        null,
        definition.selectedFields.includes('rentalId')
      ),
      new FilterDefinitionItem(
        'ARTICLE.EDIT_HEADER',
        'article',
        definition.article,
        articles,
        definition.selectedFields.includes('article')
      ),
      new FilterDefinitionItem(
        'LEASE_AGREEMENT.INVOICE_DELIVERY_METHOD',
        'invoiceDeliveryMethod',
        definition.invoiceDeliveryMethod,
        [
          { caption: 'ALL_TEXT', value: '' },
          { caption: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.Kivra), value: InvoiceDeliveryMethod.Kivra, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.Kivra) },
          { caption: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.EInvoiceB2C), value: InvoiceDeliveryMethod.EInvoiceB2C, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.EInvoiceB2C) },
          { caption: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.EmailWithPDF), value: InvoiceDeliveryMethod.EmailWithPDF, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.EmailWithPDF) },
          { caption: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.EmailWithoutPDF), value: InvoiceDeliveryMethod.EmailWithoutPDF, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.EmailWithoutPDF) },
          { caption: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.Mail), value: InvoiceDeliveryMethod.Mail, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.Mail) },
          { caption: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.Manual), value: InvoiceDeliveryMethod.Manual, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.Manual) },
          { caption: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.Sms), value: InvoiceDeliveryMethod.Sms, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.Sms) },
          { caption: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.EInvoiceB2B), value: InvoiceDeliveryMethod.EInvoiceB2B, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.EInvoiceB2B) },
          { caption: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.Avy), value: InvoiceDeliveryMethod.Avy, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.Avy) },
          { caption: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.NoDelivery), value: InvoiceDeliveryMethod.NoDelivery, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.NoDelivery) },
        ],
        definition.selectedFields.includes('invoiceDeliveryMethod')
      ),
      new FilterDefinitionItem(
        'SUBLEASE_REQUEST.SUBLEASE',
        'subleaseStatus',
        definition.subleaseStatus,
        [
          { caption: SubleaseStatusToString(SubleaseStatus.None), value: SubleaseStatus.None },
          { caption: SubleaseStatusToString(SubleaseStatus.Valid), value: SubleaseStatus.Valid },
          { caption: SubleaseStatusToString(SubleaseStatus.Coming), value: SubleaseStatus.Coming },
          { caption: SubleaseStatusToString(SubleaseStatus.Expired), value: SubleaseStatus.Expired },
        ],
        definition.selectedFields.includes('subleaseStatus')
      ),
    ];
  }
}
